import { useMemo } from 'react';
import { Select, type SelectProps } from '@mantine/core';
import { useOpenSalesOrders } from '../api';

interface SalesOrderSelectProps extends Omit<SelectProps, 'data' | 'value' | 'onChange' | 'searchable'> {
  customerId: string | undefined;
  value: string;
  onChange: (salesOrderId: string) => void;
}

/** Optional "+ sales order" picker (BUILD_SPEC.md §8.4 step 1: "pick customer (+
 * optional sales order)") — scoped to the chosen customer's OPEN sales orders
 * only. Disabled until a customer is chosen; always optional (an OUT ticket/
 * dispatch may quick-create one, or none at all — §5.7). */
export function SalesOrderSelect({ customerId, value, onChange, ...rest }: SalesOrderSelectProps) {
  const { data, isLoading } = useOpenSalesOrders(customerId);

  const options = useMemo(
    () => (data?.items ?? []).map((so) => ({ value: so.id, label: `${so.number}${so.reference ? ` — ${so.reference}` : ''}` })),
    [data],
  );

  return (
    <Select
      {...rest}
      clearable
      data={options}
      value={value || null}
      disabled={!customerId || rest.disabled}
      placeholder={!customerId ? 'Choose a customer first' : (rest.placeholder ?? 'None (spot dispatch)')}
      onChange={(newValue) => onChange(newValue ?? '')}
      nothingFoundMessage={isLoading ? 'Loading…' : 'No open sales orders for this customer'}
      comboboxProps={{ withinPortal: true }}
    />
  );
}
